import React, { useEffect, useState, Fragment } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Paper } from "@mui/material";
import MuiDatePicker from "../../Common/FormInputs/MuiDatePicker";
import Button from "../../Common/Button/Button";
import EmptyState from "../../Common/EmptyState/EmptyState";
import IncrementalPagination from "../../Common/DataTable/IncrementalPagination";
import {
    deviceStatusPageSize,
    deviceStatusPageSizeOptions,
    startDateLimit
} from "./DeviceStatusConstants";
import DisplayDeviceStatusData from "./DisplayDeviceStatusData";
import { format } from "date-fns";
import "./DeviceStatus.css";

const DeviceStatus = ({
    deviceDetails,
    getDeviceStatusData,
    statusData,
    downloadDeviceStatusReport,
    setDialog,
    setDialogContents
}) => {
    const [deviceStatusData, setDeviceStatusData] = useState([]);
    const [startDateFilter, setStartDateFilter] = useState(null);
    const [endDateFilter, setEndDateFilter] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeviceStatusDataList, setShowDeviceStatusDataList] = useState(false);
    const [isDataDownloading, setIsDataDownloading] = useState(false)
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(deviceStatusPageSize);
    const today = new Date();
    const getDefaultDateRange = () => {
        const now = new Date();
        const startOfDay = new Date(now);
        startOfDay.setHours(0, 0, 0, 0);
        return { startOfDay, now };
    };

    const handleStatusPageChange = (newPage) => {
        if (newPage === pageIndex) return;
        setPageIndex(newPage);
        fetchDeviceStatusData(newPage);
    };

    const handleStatusPageSizeChange = (newSize) => {
        if (!newSize || newSize === pageSize) return;
        setPageSize(newSize);
        setPageIndex(0);
        fetchDeviceStatusData(
            0,
            getFromTimestamp(startDateFilter),
            getToTimestamp(endDateFilter),
            newSize
        );
    };

    const getFromTimestamp = (dateFilter) => {
        if (!dateFilter) return undefined;
        const start = new Date(dateFilter);
        start.setHours(0, 0, 0, 0);
        return start.getTime();
    };

    const getToTimestamp = (dateFilter) => {
        if (!dateFilter) return undefined;
        const end = new Date(dateFilter);
        end.setHours(23, 59, 59, 999);
        return Math.min(end.getTime(), Date.now());
    };

    const fetchDeviceStatusData = (
        page = pageIndex,
        fromTs = getFromTimestamp(startDateFilter),
        toTs = getToTimestamp(endDateFilter),
        size = pageSize
    ) => {
        setIsLoading(true)
        getDeviceStatusData({
            deviceId: deviceDetails?.deviceId ?? "",
            fromTs,
            toTs,
            index: page,
            size,
        }, () => {
            setIsLoading(false);
            setShowDeviceStatusDataList(true)
        })
    };
    const reset = () => {
        const { startOfDay, now } = getDefaultDateRange();
        setStartDateFilter(startOfDay)
        setEndDateFilter(now)
        setPageIndex(0);
        fetchDeviceStatusData(0, getFromTimestamp(startOfDay), getToTimestamp(now));
    };
    const filter = () => {
        if (startDateFilter && endDateFilter && startDateFilter > endDateFilter) {
            return;
        }
        setPageIndex(0);
        fetchDeviceStatusData(0);
    };
    const handleStartDateChange = (date) => {
        setStartDateFilter(date)
        setEndDateFilter(null);
        setShowDeviceStatusDataList(false)
    }
    const handleEndDateChange = (date) => {
        setEndDateFilter(date);
        setShowDeviceStatusDataList(false)
    }
    function handleDownloadDeviceStatusData() {
        if (startDateFilter && endDateFilter && startDateFilter > endDateFilter) {
            return;
        }
        setIsDataDownloading(true)
        downloadDeviceStatusReport({
            fromTs: getFromTimestamp(startDateFilter),
            toTs: getToTimestamp(endDateFilter),
            deviceId: deviceDetails.deviceId,
            printableStartDate: `${format(startDateFilter, "dd MMM yyyy")}-${format(endDateFilter, "dd MMM yyyy")}`
        }, () => {
            setIsDataDownloading(false)
        })
    }

    useEffect(() => {
        const { startOfDay, now } = getDefaultDateRange();
        setStartDateFilter(startOfDay);
        setEndDateFilter(now);
        setIsLoading(true);
        getDeviceStatusData({ deviceId: deviceDetails?.deviceId, fromTs: getFromTimestamp(startOfDay), toTs: getToTimestamp(now), index: 0, size: pageSize },
            () => {
                setIsLoading(false);
                setShowDeviceStatusDataList(true)
            });
        setPageIndex(0);
    }, [deviceDetails?.deviceId, getDeviceStatusData])

    useEffect(() => {
        setDeviceStatusData(statusData)
    }, [statusData])

    return (
        <Fragment>
            <Box sx={{ width: "100%", minWidth: 0 }}>
                <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "minmax(0, 220px) minmax(0, 220px) auto",
                            },
                            gap: 2,
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ minWidth: 0, width: "100%" }}>
                            <MuiDatePicker
                                label="Start Date"
                                value={startDateFilter}
                                minDate={(() => {
                                    const d = new Date();
                                    return new Date(d.setDate(d.getDate() - startDateLimit));
                                })()}
                                maxDate={endDateFilter || today}
                                onChange={handleStartDateChange}
                                sx={{ width: "100%" }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0, width: "100%" }}>
                            <MuiDatePicker
                                label="End Date"
                                value={endDateFilter}
                                minDate={startDateFilter}
                                maxDate={today}
                                onChange={handleEndDateChange}
                                sx={{ width: "100%" }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                                <Button variant="primary" onClick={filter} disabled={startDateFilter === null || endDateFilter === null || startDateFilter > endDateFilter} sx={{ height: 40 }}>
                                    Filter
                                </Button>
                                <Button variant="ghost" onClick={reset} sx={{ height: 40 }}>
                                    Reset
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleDownloadDeviceStatusData}
                                    disabled={isDataDownloading || startDateFilter === null || endDateFilter === null || startDateFilter > endDateFilter}
                                    sx={{ height: 40 }}
                                >
                                    {isDataDownloading ? (
                                        <CircularProgress size="1.5rem" sx={{ color: "inherit" }} />
                                    ) : "Download"}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
                {isLoading && (
                    <Box sx={{ display: "grid", placeItems: "center", height: "20vh" }}>
                        <CircularProgress size="5em" sx={{ color: "primary.main" }} />
                    </Box>
                )}
                {!isLoading && showDeviceStatusDataList && (
                    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mt: 2, mb: 2 }}>
                        {!Array.isArray(deviceStatusData) || deviceStatusData.length === 0 ? (
                            <EmptyState
                                message="No Data found"
                                sx={{ bgcolor: "transparent", p: 0 }}
                            />
                        ) : (
                            <DisplayDeviceStatusData
                                setDialog={setDialog}
                                setDialogContents={setDialogContents}
                                deviceCategory={deviceDetails.category}
                                deviceStatusData={deviceStatusData}
                            />
                        )}
                        {Array.isArray(deviceStatusData) && deviceStatusData.length > 0 && (
                            <IncrementalPagination
                                page={pageIndex}
                                pageSize={pageSize}
                                dataLength={deviceStatusData.length}
                                onPageChange={handleStatusPageChange}
                                onPageSizeChange={handleStatusPageSizeChange}
                                pageSizeOptions={deviceStatusPageSizeOptions}
                                isLastPage={deviceStatusData.length < pageSize}
                                sx={{ m: 0, mt: 1.5, px: 0 }}
                            />
                        )}
                    </Paper>
                )}
            </Box>
        </Fragment>
    );
};

export default DeviceStatus;